/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { Capacitor } from '@capacitor/core';
import { dexieAdapter } from './adapters/dexie.adapter.js';
import { sqliteAdapter } from './adapters/sqlite.adapter.js';

/**
 * Unified Database Service
 * Automatically selects appropriate adapter based on platform
 * Provides consistent API across Web (Dexie/IndexedDB) and Native (SQLite)
 */
class DatabaseService {
  constructor() {
    this.adapter = null;
    this.platform = null;
  }

  /**
   * Initialize the database
   * Detects platform and selects appropriate adapter
   * @returns {Promise<void>}
   */
  async initialize() {
    try {
      // Platform Detection: Capacitor.getPlatform() returns:
      // - 'web': Browser (Chrome, Firefox, Safari) → uses Dexie/IndexedDB
      // - 'ios': iPhone/iPad → uses SQLite
      // - 'android': Android device → uses SQLite
      this.platform = Capacitor.getPlatform();
      console.log(`Detected platform: ${this.platform}`);

      // Adapter Selection: Route to platform-specific database implementation
      // All subsequent query/run calls will use the selected adapter
      if (this.platform === 'web') {
        console.log('Using Dexie adapter for web platform');
        this.adapter = dexieAdapter;
      } else {
        console.log('Using SQLite adapter for native platform');
        this.adapter = sqliteAdapter;
      }

      // Initialize the selected adapter
      await this.adapter.initialize();
      
      console.log(`Database initialized successfully using ${this.adapter.getName()}`);
    } catch (error) {
      console.error('Database initialization failed:', error);
      throw error;
    }
  }

  /**
   * Execute a SELECT query
   * @param {string} sql - SQL query string
   * @param {Array} params - Query parameters
   * @returns {Promise<{values: Array}>} Query results
   */
  async query(sql, params = []) {
    this._ensureInitialized();
    return await this.adapter.query(sql, params);
  }

  /**
   * Execute an INSERT, UPDATE, or DELETE command
   * @param {string} sql - SQL command string
   * @param {Array} params - Command parameters
   * @returns {Promise<{changes: {lastId: number}}>} Execution result
   */
  async run(sql, params = []) {
    this._ensureInitialized();
    return await this.adapter.run(sql, params);
  }

  /**
   * Execute raw SQL (for schema creation)
   * @param {string} sql - SQL statements
   * @returns {Promise<void>}
   */
  async execute(sql) {
    this._ensureInitialized();
    return await this.adapter.execute(sql);
  }

  /**
   * Close database connection
   * @returns {Promise<void>}
   */
  async close() {
    if (this.adapter) {
      await this.adapter.close();
      this.adapter = null;
    }
  }

  /**
   * Drop all tables and reset database schema
   * Works on both web (IndexedDB) and native (SQLite) platforms
   * @returns {Promise<void>}
   */
  async dropAllTables() {
    this._ensureInitialized();
    return await this.adapter.dropAllTables();
  }

  /**
   * Get current platform
   * @returns {string} Platform name ('web', 'ios', 'android')
   */
  getPlatform() {
    return this.platform || Capacitor.getPlatform();
  }

  /**
   * Get current adapter name
   * @returns {string} Adapter name
   */
  getAdapterName() {
    return this.adapter ? this.adapter.getName() : 'Not initialized';
  }

  /**
   * Ensure database is initialized before operations
   * @private
   * @throws {Error} If database is not initialized
   */
  _ensureInitialized() {
    if (!this.adapter) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
  }
}

// Export singleton instance
export const databaseService = new DatabaseService();
